import { useState, useEffect } from "react";
import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import { Box, Avatar, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, Typography } from "@mui/material";
import { ArrowRight, MessageCircle, Settings2, LogOut } from "lucide-react";
import { getMe, logoutUser } from "../api/api";
import "./style.css";

const getAvatarUrl = (avatar) => {
  if (!avatar) return "";
  const avatarStr = avatar.toString();
  if (avatarStr.startsWith("http")) return avatarStr;
  const isPredefined = /^[1-6]$/.test(avatarStr);
  if (isPredefined) {
    return `https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-chat/ava${avatarStr}-bg.webp`;
  }
  const baseUrl = process.env.REACT_APP_CHAT_BASE_URL || "http://localhost:5001";
  return `${baseUrl}/uploads/${avatarStr}`;
};

function Layout() {
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const isAuth = localStorage.getItem("isAuthenticated");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuth) {
      const fetchUser = async () => {
        try {
          const currentUser = await getMe();
          setUser(currentUser);
          localStorage.setItem("user", JSON.stringify(currentUser));
        } catch (error) {
          console.error("Error fetching user:", error);
          handleLogout();
        }
      };
      fetchUser();
    } else {
      setUser(null);
    }
  }, [isAuth]);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch(e) {}
    localStorage.clear();
    setUser(null);
    handleMenuClose();
    navigate("/login");
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const isMobile = window.innerWidth < 640;
  const isHome = location.pathname === "/";

  // When not on home, we show a clean white background. 
  // Home handles its own dynamic colored backgrounds.
  const noiseSvg = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E`;

  return (
    <div style={{ position: 'relative', minHeight: '100vh', width: '100vw', backgroundColor: isHome ? 'transparent' : '#FFFFFF', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      
      {/* Global Grain Overlay */}
      {!isHome && (
        <div 
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.4,
            backgroundImage: `url("${noiseSvg}")`, backgroundSize: '200px 200px', backgroundRepeat: 'repeat'
          }}
        />
      )}

      {/* GLOBAL HEADER */}
      <div style={{ 
        position: 'fixed', 
        top: isHome ? '24px' : '0', 
        left: isHome ? (isMobile ? '16px' : '32px') : '0', 
        right: isHome ? (isMobile ? '16px' : '32px') : '0', 
        padding: isHome ? '0' : (isMobile ? '16px' : '16px 32px'),
        backgroundColor: isHome ? 'transparent' : 'rgba(255, 255, 255, 0.6)',
        backdropFilter: isHome ? 'none' : 'blur(20px)',
        borderBottom: 'none',
        zIndex: 60, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: isHome ? 'white' : '#111827', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.875rem', opacity: 0.95, letterSpacing: '0.18em', textDecoration: 'none', transition: 'color 0.5s ease' }}>
          <img src="/favicon.png" alt="NEXUS 3D Logo" style={{ width: '36px', height: '36px', objectFit: 'contain', filter: isHome ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))' : 'none', transition: 'filter 0.5s ease' }} />
          NEXUS
        </Link>

        {/* Right side Auth */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {user ? (
            <>
              <Tooltip title="Profile">
                <IconButton 
                  onClick={handleMenuOpen} 
                  sx={{ 
                    p: 0.5,
                    transition: 'transform 0.2s',
                    "&:hover": { transform: 'scale(1.05)' }
                  }}
                >
                  <Avatar src={getAvatarUrl(user.avatar)} alt={user.firstName} sx={{ width: 40, height: 40, border: `2px solid ${isHome ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.1)'}`, bgcolor: 'rgba(0,0,0,0.05)', transition: 'border 0.5s ease' }} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { 
                    mt: 1.5, 
                    borderRadius: "20px", 
                    background: "rgba(255, 255, 255, 0.8)", 
                    backdropFilter: "blur(20px)", 
                    boxShadow: "0 20px 40px rgba(0,0,0,0.08)", 
                    border: "1px solid rgba(0,0,0,0.05)",
                    minWidth: 200,
                    p: 0.5
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 2, borderBottom: '1px solid rgba(0,0,0,0.05)', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827', fontFamily: "'Inter', sans-serif", lineHeight: 1.2 }}>{user.firstName} {user.lastName}</Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>@{user.username || user.firstName.toLowerCase()}</Typography>
                </Box>
                
                {location.pathname !== "/chat" && (
                  <MenuItem 
                    onClick={() => { handleMenuClose(); navigate('/chat'); }} 
                    sx={{ 
                      color: "#111827", 
                      fontWeight: 600, 
                      fontFamily: "'Inter', sans-serif",
                      borderRadius: '12px',
                      mx: 1,
                      mb: 0.5,
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.05)"
                      }
                    }}
                  >
                    <ListItemIcon><MessageCircle size={18} color="#111827" strokeWidth={2.5} /></ListItemIcon>
                    Chat
                  </MenuItem>
                )}

                <MenuItem 
                  onClick={() => { handleMenuClose(); navigate('/settings'); }} 
                  sx={{ 
                    color: "#111827", 
                    fontWeight: 600, 
                    fontFamily: "'Inter', sans-serif",
                    borderRadius: '12px',
                    mx: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.05)"
                    }
                  }}
                >
                  <ListItemIcon><Settings2 size={18} color="#111827" strokeWidth={2.5} /></ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem 
                  onClick={handleLogout} 
                  sx={{ 
                    color: "#EF4444", 
                    fontWeight: 600, 
                    fontFamily: "'Inter', sans-serif",
                    borderRadius: '12px',
                    mx: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(239, 68, 68, 0.1)"
                    }
                  }}
                >
                  <ListItemIcon><LogOut size={18} color="#EF4444" strokeWidth={2.5} /></ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Link to="/login" style={{ color: isHome ? 'white' : '#111827', textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem', transition: 'color 0.5s ease' }}>LOGIN</Link>
              <Link to="/signup" style={{ color: isHome ? 'white' : '#111827', textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem', transition: 'color 0.5s ease' }}>SIGN UP</Link>
            </>
          )}
        </div>
      </div>

      {/* OUTLET (Main Content) */}
      <div style={{ position: 'relative', zIndex: 10, height: '100vh', overflow: 'auto', paddingTop: isHome ? '0' : '70px', boxSizing: 'border-box' }}>
        <Outlet />
      </div>

      {/* GLOBAL FOOTER (Bottom-left text removed as per user request) */}

      {/* GLOBAL FOOTER (Bottom-right link) */}
      {/* We only show the bottom-right link on the Home page */}
      {isHome && (
        <Link 
          to={user ? "/chat" : "/signup"}
          style={{ 
            position: 'absolute',
            bottom: isMobile ? '24px' : '80px',
            right: isMobile ? '16px' : '40px',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            opacity: 0.95,
            textTransform: 'uppercase',
            textDecoration: 'none',
            transition: 'opacity 200ms'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.95'}
        >
          <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 'clamp(20px, 4vw, 56px)', fontWeight: 400, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {user ? "ENTER CHAT" : "JOIN NEXUS"}
          </span>
          <ArrowRight size={isMobile ? 20 : 32} strokeWidth={2.25} />
        </Link>
      )}
    </div>
  );
}

export default Layout;
